import { GameConfig } from "@/config/GameConfig";
import type { Direction, Point } from "@/core/types";
import { cellKey } from "@/levels/geometry";
import {
  detectLastSecondTurn,
  detectPerfectFold,
  isAdjacentToDistantBody,
  isSqueezedCorridorCell,
  type MovementAxis,
} from "./maneuverDetection";
import type { ManeuverAward, ManeuverType, SkillMoment } from "./types";

/** Fixed, deterministic resolution order - see `ManeuverType`'s own doc. */
const MANEUVER_ORDER: readonly ManeuverType[] = ["perfect_fold", "last_second_turn", "tail_graze", "thread_the_needle"];

/** PERFECT FOLD/TAIL GRAZE/THREAD THE NEEDLE only - LAST-SECOND TURN (PR26 V3) has its own occurrence curve, never a fixed base value; see `occurrenceBasePoints()`. */
function baseValueOf(type: Exclude<ManeuverType, "last_second_turn">): number {
  const { maneuvers } = GameConfig.scoring;
  switch (type) {
    case "perfect_fold":
      return maneuvers.perfectFold;
    case "tail_graze":
      return maneuvers.tailGraze;
    case "thread_the_needle":
      return maneuvers.threadTheNeedle;
  }
}

/** 1st/2nd/3rd credited occurrence (across the whole run, across distinct anchors) get the configured factor; 4th+ is always 0. PERFECT FOLD/TAIL GRAZE/THREAD THE NEEDLE only - see `occurrenceBasePoints()`. */
function diminishingFactor(occurrenceIndex: number): number {
  const table = GameConfig.scoring.diminishingReturns;
  return occurrenceIndex <= table.length ? table[occurrenceIndex - 1] : 0;
}

/**
 * The base points for this credited occurrence, by maneuver type. LAST-
 * SECOND TURN (PR26 V3) looks its EXACT base value up directly from
 * `lastSecondTurn.occurrenceCurve` by occurrence index (1st..15th; 16th+
 * is 0, never looked up) - every other type keeps the original
 * `baseValueOf(type) * diminishingFactor(occurrenceIndex)` (1st..3rd;
 * 4th+ is 0) completely unchanged.
 */
function occurrenceBasePoints(type: ManeuverType, occurrenceIndex: number): number {
  if (type === "last_second_turn") {
    const curve = GameConfig.scoring.lastSecondTurn.occurrenceCurve;
    return occurrenceIndex <= curve.length ? curve[occurrenceIndex - 1] : 0;
  }
  return baseValueOf(type) * diminishingFactor(occurrenceIndex);
}

function flowMultiplierForStreak(streak: number): number {
  const table = GameConfig.scoring.flow.multipliers;
  const index = Math.min(Math.max(streak, 1), table.length) - 1;
  return Math.min(GameConfig.scoring.flow.maxMultiplier, table[index]);
}

function emptyManeuverRecord(): Record<ManeuverType, number> {
  return { perfect_fold: 0, last_second_turn: 0, tail_graze: 0, thread_the_needle: 0 };
}

export interface RecordMoveInput {
  /** The run-wide executed-movement-step index this move occupies (`LevelGame.moveCount` after incrementing) - drives FLOW's gap window. */
  moveIndex: number;
  prevDirection: Direction;
  direction: Direction;
  /** Head position before this move. */
  headBefore: Point;
  /** Head position after this move (the anchor for every maneuver type). */
  headAfter: Point;
  /** The snake body after this move, fully resolved (post growth/tail-drop) - head first. */
  bodyAfterMove: readonly Point[];
  cols: number;
  rows: number;
  obstacleKeys: ReadonlySet<string>;
  /** Whether WORMHOLE was active for this specific move (captured before any bite effect this same tick could have ended it). */
  wormholeActive: boolean;
  /** Already computed via `computeManeuverSpeedWeight()` and clamped - this class never touches raw millisecond intervals. Applied to every credited maneuver EXCEPT LAST-SECOND TURN (PR26 V3) and to the DOUBLE DANGER bonus. */
  maneuverSpeedWeight: number;
  /** PR26 LAST-SECOND TURN V3: already computed via `computeLastSecondTurnSpeedMultiplier()` and clamped - this class never touches raw millisecond intervals or persistent speed state. Applied ONLY to a credited LAST-SECOND TURN award's own points, replacing `maneuverSpeedWeight` for that one type. */
  lastSecondTurnSpeedMultiplier: number;
  /** PR26 CHANGE 1 (LAST-SECOND TURN V2) - see `LastSecondTurnInput`'s own doc in `maneuverDetection.ts`. Pre-computed by `LevelGame` against production's own authoritative collision rules; this class never re-derives hazard geometry for this maneuver. */
  continuingStraightWouldBeLethal: boolean;
}

interface CorridorTrack {
  axis: MovementAxis;
  entryAnchor: Point;
}

/**
 * Per-run, framework-independent skill-scoring state: direction history
 * (PERFECT FOLD), TAIL GRAZE's rising-edge adjacency flag, THREAD THE
 * NEEDLE's in-corridor tracking, every maneuver type's anchor-dedup set
 * and diminishing-return occurrence count, the FLOW streak, (PR26 V4)
 * LAST-SECOND TURN's own armed/re-arm-progress state, and (PR26 V5) whether
 * the last credited moment was LAST-SECOND TURN alone - see
 * `resolveSkillMoment()`'s own doc for why that last one exists. Never resets on
 * eating a fruit - only a fresh instance (one per `LevelGame` run, always
 * starting armed with zero re-arm progress) starts over. `LevelGame` is
 * the only caller; it owns the geometry (board size, obstacles, live body)
 * and simply reports each safe executed move here.
 */
export class ManeuverTracker {
  private directionHistory: Direction[] = [];
  private readonly anchorsByType: Record<ManeuverType, Set<string>> = {
    perfect_fold: new Set(),
    last_second_turn: new Set(),
    tail_graze: new Set(),
    thread_the_needle: new Set(),
  };
  private readonly occurrenceCountByType: Record<ManeuverType, number> = emptyManeuverRecord();
  private tailGrazeAdjacentLastStep = false;
  private corridor: CorridorTrack | null = null;
  private lastCreditedMoveIndex: number | null = null;
  /**
   * PR26 LAST-SECOND TURN V5: whether the immediately preceding CREDITED
   * moment consisted of LAST-SECOND TURN alone (no other maneuver type
   * credited that same step - i.e. not a DOUBLE DANGER combo). Used only
   * to gate FLOW continuation into the NEXT moment - see
   * `resolveSkillMoment()`'s own doc.
   */
  private lastCreditedWasPureLastSecondTurn = false;
  private currentFlowStreakValue = 0;
  private bestFlowStreakValue = 0;
  private skillScoreTotal = 0;
  private doubleDangerTotal = 0;
  /**
   * PR26 LAST-SECOND TURN V4 re-arm state - see `GameConfig.scoring.lastSecondTurn.reArmSafeMoves`'s
   * own doc. Starts armed (a fresh run/instance always starts armed - see
   * this class's own doc on lifecycle). `reArmProgressValue` is only ever
   * meaningful while disarmed - it is reset to 0 the instant the tracker
   * re-arms, and never incremented while already armed.
   */
  private lastSecondTurnArmedValue = true;
  private reArmProgressValue = 0;

  /** Reports one safe executed movement step. Returns the skill moment awarded, or `null` if nothing was credited (nothing detected, every candidate was a duplicate anchor, every candidate was an exhausted 4th+/16th+ occurrence, or the only candidate was a LAST-SECOND TURN detected while disarmed). Never call this for a lethal/unsafe move. */
  recordMove(input: RecordMoveInput): SkillMoment | null {
    this.directionHistory.push(input.direction);
    if (this.directionHistory.length > 3) this.directionHistory.shift();

    const candidates = this.detectCandidates(input);

    const credited: ManeuverAward[] = [];
    for (const type of MANEUVER_ORDER) {
      const candidate = candidates.find((c) => c.type === type);
      if (!candidate) continue;

      // PR26 LAST-SECOND TURN V4: a candidate detected while disarmed is
      // never credited - no anchor consumed, no occurrence counted, no
      // points. It may still have been "detected" (the candidate exists),
      // but competitively it is exactly as if it never happened, so the
      // 15-occurrence curve and anchor-dedup sets are never touched by it.
      if (type === "last_second_turn" && !this.lastSecondTurnArmedValue) continue;

      const anchorKey = cellKey(candidate.anchor);
      const anchors = this.anchorsByType[type];
      if (anchors.has(anchorKey)) continue; // duplicate anchor: 0 impact, not even an occurrence.
      anchors.add(anchorKey);

      const occurrenceIndex = this.occurrenceCountByType[type] + 1;
      this.occurrenceCountByType[type] = occurrenceIndex;

      const points = occurrenceBasePoints(type, occurrenceIndex);
      if (points <= 0) continue; // exhausted occurrence (4th+, or 16th+ for LAST-SECOND TURN V3): still counted, still closes the anchor, worth nothing further.

      credited.push({ type, anchor: candidate.anchor, points });
    }

    // Re-arm bookkeeping runs on EVERY move, whether or not anything was
    // credited this step (an uncredited disarmed LAST-SECOND TURN attempt
    // is itself a dangerous-forward tick, so it must still reset re-arm
    // progress) - see `updateReArmState()`'s own doc.
    this.updateReArmState(!input.continuingStraightWouldBeLethal, credited.some((award) => award.type === "last_second_turn"));

    if (credited.length === 0) return null;
    return this.resolveSkillMoment(input.moveIndex, credited, input.maneuverSpeedWeight, input.lastSecondTurnSpeedMultiplier);
  }

  /**
   * PR26 LAST-SECOND TURN V4 re-arm state machine.
   * `safeForward` is exactly `!continuingStraightWouldBeLethal` for this
   * move - the SAME production collision/hazard evaluation LAST-SECOND
   * TURN's own detection already uses (see `LastSecondTurnInput`'s own doc
   * in `maneuverDetection.ts`), never a second approximate hazard check.
   *
   * A REWARDED LAST-SECOND TURN this step always disarms immediately and
   * zeroes progress, regardless of `safeForward` (a rewarded LAST-SECOND
   * TURN is, by construction, a dangerous-forward tick - continuing
   * straight would have been lethal - so this is never in tension with the
   * "danger resets progress" rule below, only a stronger, explicit version
   * of it for the one case that also needs to flip `armed` off).
   *
   * Otherwise: while armed, nothing to do (progress is meaningless while
   * armed). While disarmed, a safe-forward tick advances progress toward
   * the required count, re-arming (and zeroing progress) the instant it's
   * reached; any dangerous-forward tick (an uncredited candidate while
   * disarmed is always exactly this) resets progress to 0 - "consecutive
   * means consecutive", never a running total.
   */
  private updateReArmState(safeForward: boolean, lastSecondTurnCreditedThisStep: boolean): void {
    if (lastSecondTurnCreditedThisStep) {
      this.lastSecondTurnArmedValue = false;
      this.reArmProgressValue = 0;
      return;
    }
    if (this.lastSecondTurnArmedValue) return;
    if (!safeForward) {
      this.reArmProgressValue = 0;
      return;
    }
    this.reArmProgressValue += 1;
    if (this.reArmProgressValue >= GameConfig.scoring.lastSecondTurn.reArmSafeMoves) {
      this.lastSecondTurnArmedValue = true;
      this.reArmProgressValue = 0;
    }
  }

  private detectCandidates(input: RecordMoveInput): { type: ManeuverType; anchor: Point }[] {
    const candidates: { type: ManeuverType; anchor: Point }[] = [];

    if (detectPerfectFold(this.directionHistory, input.bodyAfterMove.length, GameConfig.scoring.perfectFold.minLength)) {
      candidates.push({ type: "perfect_fold", anchor: input.headAfter });
    }

    if (
      detectLastSecondTurn({
        prevDirection: input.prevDirection,
        direction: input.direction,
        continuingStraightWouldBeLethal: input.continuingStraightWouldBeLethal,
      })
    ) {
      candidates.push({ type: "last_second_turn", anchor: input.headAfter });
    }

    const adjacentNow = isAdjacentToDistantBody(input.headAfter, input.bodyAfterMove);
    if (adjacentNow && !this.tailGrazeAdjacentLastStep) {
      candidates.push({ type: "tail_graze", anchor: input.headAfter });
    }
    this.tailGrazeAdjacentLastStep = adjacentNow;

    const threadCandidate = this.updateCorridorTracking(input);
    if (threadCandidate) candidates.push(threadCandidate);

    return candidates;
  }

  /** THREAD THE NEEDLE's state machine: start tracking on safe entry into a squeezed cell, keep tracking only along the same axis, and resolve a candidate the step the snake safely exits the squeezed section - never before, and never at all if the axis changes mid-corridor. */
  private updateCorridorTracking(input: RecordMoveInput): { type: ManeuverType; anchor: Point } | null {
    const axis: MovementAxis = input.direction === "up" || input.direction === "down" ? "v" : "h";
    const bodyKeys = new Set(input.bodyAfterMove.map(cellKey));
    const squeezed = isSqueezedCorridorCell({
      headAfter: input.headAfter,
      direction: input.direction,
      cols: input.cols,
      rows: input.rows,
      obstacleKeys: input.obstacleKeys,
      bodyKeys,
      wormholeActive: input.wormholeActive,
    });

    if (this.corridor === null) {
      if (squeezed) this.corridor = { axis, entryAnchor: input.headAfter };
      return null;
    }
    if (this.corridor.axis !== axis) {
      this.corridor = squeezed ? { axis, entryAnchor: input.headAfter } : null;
      return null;
    }
    if (squeezed) return null; // still inside the same-axis squeezed run.

    const entryAnchor = this.corridor.entryAnchor;
    this.corridor = null;
    return { type: "thread_the_needle", anchor: entryAnchor };
  }

  private resolveSkillMoment(moveIndex: number, credited: ManeuverAward[], rawSpeedWeight: number, rawLastSecondTurnSpeedMultiplier: number): SkillMoment {
    const doubleDanger = credited.length >= 2;
    if (doubleDanger) this.doubleDangerTotal += 1;
    const rawPoints = credited.reduce((sum, award) => sum + award.points, 0) + (doubleDanger ? GameConfig.scoring.maneuvers.doubleDangerBonus : 0);

    // PR26 LAST-SECOND TURN V5: a moment made up of LAST-SECOND TURN alone
    // (not combined with another maneuver type via DOUBLE DANGER) must
    // never inherit/advance a FLOW streak from a PRECEDING moment that was
    // ALSO LAST-SECOND TURN alone - repeated LAST-SECOND cannot maintain
    // FLOW by itself, full stop. A different credited maneuver moment
    // (mixed chain, e.g. LAST-SECOND -> THREAD THE NEEDLE -> LAST-SECOND)
    // still continues FLOW under the normal gap rule - only a DIRECT
    // LAST-SECOND-to-LAST-SECOND transition is excluded. This is deliberately
    // independent of the re-arm gap (`reArmSafeMoves`) - re-arm alone
    // already pushes the minimum gap past FLOW's window today, but this
    // rule is the one actually responsible for the behavior and must hold
    // even if `reArmSafeMoves` is ever retuned.
    const isPureLastSecondTurn = credited.length === 1 && credited[0].type === "last_second_turn";
    const gap = this.lastCreditedMoveIndex === null ? Infinity : moveIndex - this.lastCreditedMoveIndex;
    const blockedLastSecondTurnChain = isPureLastSecondTurn && this.lastCreditedWasPureLastSecondTurn;
    const continuesFlow = gap <= GameConfig.scoring.flow.windowMoves && !blockedLastSecondTurnChain;
    this.currentFlowStreakValue = continuesFlow ? this.currentFlowStreakValue + 1 : 1;
    this.lastCreditedMoveIndex = moveIndex;
    this.lastCreditedWasPureLastSecondTurn = isPureLastSecondTurn;
    this.bestFlowStreakValue = Math.max(this.bestFlowStreakValue, this.currentFlowStreakValue);
    const flowMultiplier = flowMultiplierForStreak(this.currentFlowStreakValue);

    const { min, max } = GameConfig.scoring.maneuverSpeedWeight;
    const maneuverSpeedWeight = Math.min(max, Math.max(min, rawSpeedWeight));

    const { min: lstMin, max: lstMax } = GameConfig.scoring.lastSecondTurn.speedMultiplier;
    const lastSecondTurnSpeedMultiplier = Math.min(lstMax, Math.max(lstMin, rawLastSecondTurnSpeedMultiplier));

    // PR26 LAST-SECOND TURN V3: this ONE maneuver type's own points are
    // weighted by its own speed multiplier, never the shared
    // maneuverSpeedWeight - every OTHER credited maneuver's points, plus
    // the flat DOUBLE DANGER combo bonus (not any single type's own
    // reward component), keep using the shared weight exactly as before.
    const weightedPoints =
      credited.reduce((sum, award) => sum + award.points * (award.type === "last_second_turn" ? lastSecondTurnSpeedMultiplier : maneuverSpeedWeight), 0) +
      (doubleDanger ? GameConfig.scoring.maneuvers.doubleDangerBonus * maneuverSpeedWeight : 0);

    const awardedPoints = Math.round(weightedPoints * flowMultiplier);
    this.skillScoreTotal += awardedPoints;

    return {
      moveIndex,
      maneuvers: credited,
      doubleDanger,
      flowStreak: this.currentFlowStreakValue,
      flowMultiplier,
      maneuverSpeedWeight,
      lastSecondTurnSpeedMultiplier,
      rawPoints,
      awardedPoints,
    };
  }

  get skillScore(): number {
    return this.skillScoreTotal;
  }

  get maneuverCounts(): Record<ManeuverType, number> {
    return { ...this.occurrenceCountByType };
  }

  get doubleDangerCount(): number {
    return this.doubleDangerTotal;
  }

  get bestFlowStreak(): number {
    return this.bestFlowStreakValue;
  }

  get currentFlowStreak(): number {
    return this.currentFlowStreakValue;
  }

  get currentFlowMultiplier(): number {
    return flowMultiplierForStreak(this.currentFlowStreakValue || 1);
  }

  /** PR26 LAST-SECOND TURN V4: whether a LAST-SECOND TURN detected right now would actually be credited. `true` at the start of a fresh run/instance. */
  get lastSecondTurnArmed(): boolean {
    return this.lastSecondTurnArmedValue;
  }

  /** PR26 LAST-SECOND TURN V4: consecutive safe-forward ticks accumulated so far while disarmed - always 0 while armed. */
  get lastSecondTurnReArmProgress(): number {
    return this.reArmProgressValue;
  }
}
